import { useState, useEffect } from 'react';
import apiService, { getStorageUrl } from '../../../../services/apiService';
import { useTenant } from '../../../../contexts/TenantContext';

const ThrowballAccounts = () => {
  const { siteSettings } = useTenant();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/public/accounts');

      if (response.data.success && response.data.data) {
        setAccounts(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileUrl = (filePath) => {
    if (!filePath) return '#';
    return getStorageUrl(filePath) || '#';
  };

  return (
    <section className="tb-page-section tb-accounts-section">
      <div className="tb-container">
        {/* Top Banner */}
        <div className="tb-page-banner">
          <img src={siteSettings?.page_banners?.accounts || '/theme/assets/images/banner-4.jpg'} alt="Accounts Banner" />
        </div>

        {/* Page Content */}
        <div className="tb-page-content">
          <div className="tb-heading-top">
            <h1 className="tb-page-title">ACCOUNTS</h1>
            <span className="tb-mini-line"></span>
          </div>

          {loading ? (
            <div className="accounts-loading">
              <i className="fas fa-spinner fa-spin"></i>
              <p>Loading accounts...</p>
            </div>
          ) : accounts.length === 0 ? (
            <div className="accounts-empty">
              <i className="fas fa-file-invoice"></i>
              <p>No account documents available at the moment.</p>
            </div>
          ) : (
            <div className="accounts-list">
              {accounts.map((account, index) => (
                <a
                  key={account.id || index}
                  href={getFileUrl(account.file_path)}
                  download
                  className="account-item"
                >
                  <div className="account-icon">
                    <i className="fas fa-file-pdf"></i>
                  </div>
                  <div className="account-info">
                    <span className="account-title">{account.title || account.file_name}</span>
                    {account.file_size && (
                      <span className="account-size">({formatFileSize(account.file_size)})</span>
                    )}
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .tb-accounts-section .tb-page-content {
          padding: 2rem 1rem;
        }

        .accounts-loading,
        .accounts-empty {
          text-align: center;
          padding: 60px 20px;
        }

        .accounts-loading i,
        .accounts-empty i {
          font-size: 3rem;
          color: #c00f15;
          margin-bottom: 16px;
          display: block;
        }

        .accounts-loading p,
        .accounts-empty p {
          color: #666;
          font-size: 1.1rem;
        }

        .accounts-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-width: 800px;
          margin: 0 auto;
        }

        .account-item {
          display: flex;
          align-items: center;
          padding: 20px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          text-decoration: none !important;
          transition: all 0.2s ease;
          gap: 16px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .account-item * {
          text-decoration: none !important;
        }

        .account-item:hover {
          border-color: #c00f15;
          box-shadow: 0 4px 12px rgba(192, 15, 21, 0.1);
          transform: translateY(-2px);
        }

        .account-icon {
          width: 50px;
          height: 50px;
          background: #fef2f2;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .account-icon i {
          font-size: 24px;
          color: #dc3545;
        }

        .account-info {
          flex: 1;
          min-width: 0;
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .account-title {
          color: #1f2937;
          font-size: 1rem;
          font-weight: 500;
          text-decoration: none;
        }

        .account-item:hover .account-title {
          text-decoration: none;
        }

        .account-size {
          color: #9ca3af;
          font-size: 0.9rem;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .account-item {
            padding: 16px;
            gap: 12px;
          }

          .account-icon {
            width: 40px;
            height: 40px;
          }

          .account-icon i {
            font-size: 20px;
          }

          .account-title {
            font-size: 0.95rem;
          }
        }

        @media (max-width: 480px) {
          .account-item {
            padding: 14px;
          }

          .account-title {
            font-size: 0.9rem;
          }

          .account-size {
            font-size: 0.8rem;
          }
        }
      `}</style>
    </section>
  );
};

export default ThrowballAccounts;
